import { useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { HeaderBox, PageContainer } from './styled';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { Term } from './types/term';
import TermsTable from './TermsTable';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import terms from '../../apis/terms';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';
import AddTermModal from './AddTermModal';
import EditTermModal from './EditTermModal';
import { StyledButton } from '../Exams/styled';
import Icons from '../../components/icons';

const TermsContainer = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [termsData, setTermsData] = useState<Term[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // SnackBar
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await terms.getTerms(loggedInUser.school);
      setTermsData(response as any);
    } catch (error) {
      console.error(error);
      showToast('Failed to load terms.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  }, [loggedInUser.school]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const handleEditOpenModal = (term: Term) => {
    setSelectedTerm(term);
    setIsEditModalOpen(true);
  };

  const handleEditCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedTerm(null);
  };

  const handleDeleteClick = (term: Term) => {
    setSelectedTerm(term);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedTerm(null);
  };

  const handleApiSuccessAndFailure = (isSuccess: boolean, message: string) => {
    setIsAddModalOpen(false);
    handleEditCloseModal();
    showToast(
      message,
      isSuccess ? SnackbarSeverityType.success : SnackbarSeverityType.error
    );
  };

  const handleDeleteConfirm = async () => {
    if (selectedTerm == null || !selectedTerm.id) {
      showToast('No term selected for deletion.', SnackbarSeverityType.error);
      handleDeleteCancel();
      return;
    }
    setIsDeleting(true);
    try {
      const response = await terms.deleteTerm(selectedTerm.id);
      if (response) {
        setTermsData((prev) =>
          prev?.filter((item: Term) => item.id !== selectedTerm.id)
        );

        showToast(
          `Term "${selectedTerm.name}" deleted successfully`,
          SnackbarSeverityType.success
        );
      }
    } catch (error: any) {
      const errorMsg =
        error?.errors?.[0]?.message || `Error deleting "${selectedTerm.name}"`;
      showToast(errorMsg, SnackbarSeverityType.error);
    }

    setIsDeleting(false);
    handleDeleteCancel();
  };

  return (
    <>
      <PageContainer>
        <HeaderBox>
          <Typography variant='h4' fontWeight='bold'>
            Terms
          </Typography>
          <StyledButton
            variant='contained'
            onClick={() => setIsAddModalOpen(true)}
          >
            <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Term
          </StyledButton>
        </HeaderBox>

        <TermsTable
          isLoading={isLoading}
          terms={termsData || []}
          onEdit={(term) => handleEditOpenModal(term)}
          onDelete={handleDeleteClick}
        />

        <AddTermModal
          fetchData={fetchData}
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
        />

        <EditTermModal
          fetchData={fetchData}
          open={isEditModalOpen}
          onClose={handleEditCloseModal}
          term={selectedTerm}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
        />

        <ConfirmationDialog
          open={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          message={`Are you sure you want to delete "${
            selectedTerm?.name || ''
          }"?`}
          disable={isDeleting}
        />
      </PageContainer>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default TermsContainer;
